(() => {
  const existing = window.themeAssets || {};
  const entryScript = document.currentScript;
  const entryUrl = new URL(entryScript?.src || document.baseURI, document.baseURI);
  const assetVersion = entryUrl.search;
  entryUrl.search = '';
  entryUrl.hash = '';

  const assetBaseUrl = new URL('./', entryUrl).href;
  const scriptPromises = existing.scriptPromises || new Map();
  const stylePromises = existing.stylePromises || new Map();
  const runtimePromises = existing.runtimePromises || new WeakMap();
  let coreReady;

  const assetUrl = (filename) => {
    const url = new URL(filename, assetBaseUrl);
    if (assetVersion) url.search = assetVersion;
    return url.href;
  };

  const loadScript = (filename, options = {}) => {
    const { parent = document.head, cache = true, attributes = {} } = options;
    const url = assetUrl(filename);

    if (cache && scriptPromises.has(url)) return scriptPromises.get(url);

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = false;
      script.dataset.themeAsset = filename;

      Object.entries(attributes).forEach(([name, value]) => {
        script.setAttribute(name, value);
      });

      script.addEventListener('load', () => resolve(script), { once: true });
      script.addEventListener(
        'error',
        () => reject(new Error(`No fue posible cargar ${filename}.`)),
        { once: true },
      );

      (parent || document.head || document.documentElement).appendChild(script);
    });

    if (cache) {
      scriptPromises.set(url, promise);
      promise.catch(() => scriptPromises.delete(url));
    }

    return promise;
  };

  const loadStyle = (filename) => {
    const url = assetUrl(filename);
    if (stylePromises.has(url)) return stylePromises.get(url);

    const targetUrl = new URL(url);
    targetUrl.search = '';
    targetUrl.hash = '';
    const existingLink = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]'),
    ).find((link) => {
      try {
        const href = new URL(link.href, document.baseURI);
        href.search = '';
        href.hash = '';
        return href.href === targetUrl.href;
      } catch (_error) {
        return false;
      }
    });

    if (existingLink?.sheet) return Promise.resolve(existingLink);

    const promise = new Promise((resolve, reject) => {
      const link = existingLink || document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.dataset.themeAsset = filename;

      link.addEventListener('load', () => resolve(link), { once: true });
      link.addEventListener(
        'error',
        () => reject(new Error(`No fue posible cargar ${filename}.`)),
        { once: true },
      );

      if (!existingLink) document.head.appendChild(link);
    });

    stylePromises.set(url, promise);
    promise.catch(() => stylePromises.delete(url));
    return promise;
  };

  const manageSwiper = () => {
    if (!window.Swiper || window.Swiper.__jdManaged) return window.Swiper;

    const NativeSwiper = window.Swiper;
    function ManagedSwiper(target, parameters) {
      const element =
        typeof target === 'string' ? document.querySelector(target) : target;
      const existingInstance = element?.swiper;

      if (existingInstance && !existingInstance.destroyed) {
        existingInstance.update?.();
        return existingInstance;
      }

      return new NativeSwiper(target, parameters);
    }

    Object.setPrototypeOf(ManagedSwiper, NativeSwiper);
    ManagedSwiper.prototype = NativeSwiper.prototype;
    Object.defineProperty(ManagedSwiper, '__jdManaged', { value: true });
    Object.defineProperty(ManagedSwiper, '__jdNative', { value: NativeSwiper });
    window.Swiper = ManagedSwiper;
    return ManagedSwiper;
  };

  const api = {
    ...existing,
    assetBaseUrl,
    assetUrl,
    scriptPromises,
    stylePromises,
    runtimePromises,
    loadScript,
    loadStyle,
    runScript(filename, parent = document.head) {
      return loadScript(filename, { parent, cache: false });
    },
    loadCore() {
      return coreReady;
    },
    loadSwiper() {
      if (window.Swiper) return Promise.resolve(manageSwiper());

      return Promise.all([
        loadStyle('swiper-vendor.min.css'),
        loadScript('swiper-vendor.min.js'),
      ]).then(() => {
        if (!window.Swiper) {
          throw new Error('Swiper no quedó disponible después de cargar su runtime.');
        }
        return manageSwiper();
      });
    },
    loadGsap() {
      if (window.gsap) return Promise.resolve(window.gsap);

      return loadScript('gsap-vendor.min.js').then(() => {
        if (!window.gsap) {
          throw new Error('GSAP no quedó disponible después de cargar su runtime.');
        }
        return window.gsap;
      });
    },
    loadTypewriter() {
      if (window.Typewriter) return Promise.resolve(window.Typewriter);

      return loadScript('typewriter-vendor.js').then(() => {
        if (!window.Typewriter) {
          throw new Error(
            'Typewriter no quedó disponible después de cargar su runtime.',
          );
        }
        return window.Typewriter;
      });
    },
    loadProductCard() {
      return coreReady.then(() => loadScript('product-card-runtime.js'));
    },
    loadGlobalProduct() {
      return coreReady
        .then(() => loadScript('global-product.js'))
        .then(() => window.ensureJDGlobalProduct?.() || window.JDGlobalProductReady);
    },
  };

  window.themeAssets = api;

  const coreFiles = [
    'global-core-accessibility.js',
    'global-core-shopify.js',
    'global-core-navigation.js',
    'global-core-media.js',
    'global-core-init.js',
  ];

  coreReady = coreFiles
    .reduce(
      (promise, filename) => promise.then(() => loadScript(filename)),
      Promise.resolve(),
    )
    .then(() => {
      window.dispatchEvent(new CustomEvent('theme:core:ready'));
      return true;
    });

  window.themeCoreReady = coreReady;
  window.loadSwiperRuntime = (filename, source = document.currentScript) => {
    const parent = source?.parentElement || document.head;
    const key = `jdRuntime${filename
      .replace(/[^a-z0-9]+(.)?/gi, (_match, character) =>
        character ? character.toUpperCase() : '',
      )
      .replace(/^./, (character) => character.toUpperCase())}`;
    let parentPromises = runtimePromises.get(parent);

    if (!parentPromises) {
      parentPromises = new Map();
      runtimePromises.set(parent, parentPromises);
    }
    if (parentPromises.has(filename)) return parentPromises.get(filename);

    if (parent?.dataset) parent.dataset[key] = 'loading';
    const promise = Promise.all([coreReady, api.loadSwiper()])
      .then(() => api.runScript(filename, parent))
      .then(() => {
        if (parent?.dataset) parent.dataset[key] = 'loaded';
      })
      .catch((error) => {
        parentPromises.delete(filename);
        if (parent?.dataset) delete parent.dataset[key];
        throw error;
      });

    parentPromises.set(filename, promise);
    return promise;
  };

  coreReady.catch((error) => {
    console.error('No fue posible cargar el núcleo JavaScript del tema.', error);
  });

  window.dispatchEvent(new CustomEvent('theme:assets:ready'));
  window.dispatchEvent(new CustomEvent('theme:swiper-loader:ready'));
})();


/* JD_FUNNEL_INTERACTION_FIX_START */
(() => {
  const styleId = 'jd-funnel-interaction-fix-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .hero__buttons {
        position: relative;
        z-index: 5;
        pointer-events: auto;
      }
      .hero__buttons a[href^="https://app.juandiaz.mx"] {
        position: relative;
        z-index: 6;
        pointer-events: auto;
        touch-action: manipulation;
      }
      .pricing-table__note {
        display: none !important;
      }
      .pricing-table__item--price .boton-forzado-pricing {
        margin-top: auto;
      }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  const applyJdFunnelFixes = (root = document) => {
    root.querySelectorAll?.('.hero__buttons a[href^="https://app.juandiaz.mx"]').forEach((link) => {
      if (link.dataset.jdDirectNavigation === 'true') return;
      link.dataset.jdDirectNavigation = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(link.href);
      });
    });

    root.querySelectorAll?.('.pricing-table__note').forEach((note) => note.remove());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyJdFunnelFixes(document), { once: true });
  } else {
    applyJdFunnelFixes(document);
  }

  document.addEventListener('shopify:section:load', (event) => applyJdFunnelFixes(event.target));
})();
/* JD_FUNNEL_INTERACTION_FIX_END */

/* JD_MOBILE_LEADFINDER_CTA_PROXY_START */
(() => {
  const CTA_SELECTOR = '.hero__buttons a[href^="https://app.juandiaz.mx"]';
  const PROXY_ID = 'jd-mobile-leadfinder-cta-proxy';
  const mobileMedia = window.matchMedia?.('(max-width: 989px)');
  let proxy = null;
  let frameRequest = 0;
  let observedLink = null;
  let resizeObserver = null;

  const isMobile = () =>
    mobileMedia ? mobileMedia.matches : window.innerWidth <= 989;

  const getVisibleCta = () =>
    Array.from(document.querySelectorAll(CTA_SELECTOR)).find((link) => {
      const rect = link.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      const style = window.getComputedStyle(link);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    }) || null;

  const ensureProxy = () => {
    if (proxy?.isConnected) return proxy;

    proxy = document.getElementById(PROXY_ID) || document.createElement('a');
    proxy.id = PROXY_ID;
    proxy.setAttribute('aria-hidden', 'true');
    proxy.tabIndex = -1;
    proxy.dataset.jdMobileCtaProxy = 'true';
    Object.assign(proxy.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '0',
      height: '0',
      display: 'block',
      zIndex: '2147483647',
      opacity: '0',
      background: 'transparent',
      border: '0',
      padding: '0',
      margin: '0',
      pointerEvents: 'none',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent',
    });

    if (!proxy.isConnected) (document.body || document.documentElement).appendChild(proxy);
    return proxy;
  };

  const hideProxy = () => {
    if (!proxy) return;
    proxy.style.pointerEvents = 'none';
    proxy.style.width = '0';
    proxy.style.height = '0';
  };

  const observeLink = (link) => {
    if (observedLink === link || typeof ResizeObserver !== 'function') return;
    resizeObserver?.disconnect();
    observedLink = link;
    resizeObserver = new ResizeObserver(() => requestSync());
    resizeObserver.observe(link);
  };

  const syncProxy = () => {
    frameRequest = 0;
    if (!isMobile()) {
      hideProxy();
      return;
    }

    const link = getVisibleCta();
    if (!link) {
      hideProxy();
      return;
    }

    const rect = link.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const intersectsViewport =
      rect.right > 0 &&
      rect.bottom > 0 &&
      rect.left < viewportWidth &&
      rect.top < viewportHeight;

    if (!intersectsViewport) {
      hideProxy();
      return;
    }

    const activeProxy = ensureProxy();
    activeProxy.href = link.href;
    if (link.target) activeProxy.target = link.target;
    else activeProxy.removeAttribute('target');
    if (link.rel) activeProxy.rel = link.rel;
    else activeProxy.removeAttribute('rel');

    activeProxy.style.left = `${rect.left}px`;
    activeProxy.style.top = `${rect.top}px`;
    activeProxy.style.width = `${rect.width}px`;
    activeProxy.style.height = `${rect.height}px`;
    activeProxy.style.pointerEvents = 'auto';
    observeLink(link);
  };

  const requestSync = () => {
    if (frameRequest) return;
    frameRequest = window.requestAnimationFrame(syncProxy);
  };

  window.addEventListener('scroll', requestSync, { passive: true, capture: true });
  window.addEventListener('resize', requestSync, { passive: true });
  window.addEventListener('orientationchange', requestSync, { passive: true });
  window.visualViewport?.addEventListener('scroll', requestSync, { passive: true });
  window.visualViewport?.addEventListener('resize', requestSync, { passive: true });
  mobileMedia?.addEventListener?.('change', requestSync);
  document.addEventListener('shopify:section:load', requestSync);
  document.addEventListener('shopify:section:reorder', requestSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', requestSync, { once: true });
  } else {
    requestSync();
  }
  window.addEventListener('load', requestSync, { once: true });
})();
/* JD_MOBILE_LEADFINDER_CTA_PROXY_END */

/* JD_PRICING_STRIKE_SCOPE_START */
(() => {
  const PRICE_SELECTOR = '.pricing-table__item--price [data-pt-price]';
  const STYLE_ID = 'jd-pricing-strike-scope-styles';
  const observers = new WeakMap();

  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .pricing-table__item--price .pricing-table__item-price {
        text-decoration: none !important;
      }
      .pricing-table__item--price .pricing-table__item-price .jd-pricing-price-value {
        text-decoration-line: line-through;
        text-decoration-thickness: 0.1em;
        text-decoration-color: currentColor;
      }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  const formatPrice = (element) => {
    const rawValue = element.textContent || '';
    if (!rawValue.trim()) return;

    const existingValue = element.querySelector(':scope > .jd-pricing-price-value');
    if (existingValue && element.dataset.jdPricingStrikeValue === rawValue) return;

    const slashIndex = rawValue.indexOf('/');
    const strikeValue = (slashIndex >= 0 ? rawValue.slice(0, slashIndex) : rawValue).trim();
    const periodValue = slashIndex >= 0 ? rawValue.slice(slashIndex + 1).trim() : '';
    if (!strikeValue) return;

    const strike = document.createElement('span');
    strike.className = 'jd-pricing-price-value';
    strike.textContent = strikeValue;

    element.replaceChildren(strike);
    if (periodValue) element.append(document.createTextNode(` / ${periodValue}`));
    element.dataset.jdPricingStrikeValue = element.textContent || '';
  };

  const bindPrice = (element) => {
    formatPrice(element);
    if (observers.has(element) || typeof MutationObserver !== 'function') return;

    const observer = new MutationObserver(() => formatPrice(element));
    observer.observe(element, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    observers.set(element, observer);
  };

  const applyPricingStrikeScope = (root = document) => {
    root.querySelectorAll?.(PRICE_SELECTOR).forEach(bindPrice);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyPricingStrikeScope(document), { once: true });
  } else {
    applyPricingStrikeScope(document);
  }

  document.addEventListener('shopify:section:load', (event) => applyPricingStrikeScope(event.target));
})();
/* JD_PRICING_STRIKE_SCOPE_END */
